import type { IconDefinition } from "@fortawesome/pro-regular-svg-icons";
import type { NavigateFunction } from "react-router-dom";

import type { CopilotResourceRef } from "src/clients/agent-management";
import type { NodeIconColor } from "src/design-system/NodeIcon";
import type { ReferenceContextualEntityType } from "src/unifiedCopilot/context/entityDefinitions";
import type {
  ContextualEntityResolver,
  EntityResolutionContext,
} from "src/unifiedCopilot/context/entityResolution";

/**
 * Contextual entities have two independent dimensions: source and usage.
 *
 * Sources:
 * - Ambient entities are provided by the currently mounted page/components.
 * - Global contextual entities remain available regardless of the page the
 *   user is viewing.
 * A persisted resource may come from both sources; matching resources are
 * deduplicated by `contextualEntityKey`.
 *
 * Usages:
 * - Persisted reference entities from either source are available to mention.
 * - Only ambient entities may be added to the agent context automatically,
 *   and each ambient provider explicitly chooses whether to opt in.
 * - Inclusion is additive: if any ambient provider opts in, the entity is
 *   automatically included.
 *
 * Inline entities only exist ambiently
 */

// String union with all the types that can be added as context
// as an inline contextual entity
export type InlineContextualEntityType = "selection";

export type ContextualEntityType =
  | ReferenceContextualEntityType
  | InlineContextualEntityType;

export type ContextualEntityRef = {
  type: ContextualEntityType;
  id: string;
  // The ancestor chain, e.g. a node's flow-version which itself belongs to a
  // flow — deeplinks resolve whatever part of the chain is carried here.
  parent?: ContextualEntityRef;
};

type ContextualEntityBase = {
  id: string;
  // Useful for the user to refer to an entity by it
  displayName: string;
  // To represent hierarchical relationships between entities, e.g. a node within a flow
  parent?: ContextualEntityRef;
  // Whether the user may drop the entity from the composer's automatic context;
  // a page registers its primary entity with `false`. Defaults to true
  dismissible?: boolean;
};

/**
 * For entities that are better represented by a reference to a persisted object, e.g. a flow
 */
export type ReferenceContextualEntity = ContextualEntityBase & {
  kind: "reference";
  type: ReferenceContextualEntityType;
  /**
   * Type-specific discriminator used for presentation only, e.g. a node's
   * `NODE_TYPE` to pick its icon. Never sent to the agent.
   */
  subtype?: string;
};

/**
 * Content that only exists in the browser (unsaved edits, a text selection).
 * A snapshot of whatever was last registered. Keep `content` small to avoid
 * context bloat
 */
export type InlineContextualEntity = ContextualEntityBase & {
  kind: "inline";
  type: InlineContextualEntityType;
  content: unknown;
};

export type ContextualEntity =
  | ReferenceContextualEntity
  | InlineContextualEntity;

/**
 * Controls usage for one ambient provider. Mentionability follows from being a
 * registered reference; automatic agent context is the additional opt-in.
 */
export type AmbientContextualEntityRegistrationOptions = {
  includeInAgentContext?: boolean;
};

export type ContextualEntityKey = string;

export const contextualEntityKey = (
  entity: ContextualEntity,
): ContextualEntityKey => {
  const ownKey = `${entity.type}:${entity.id}`;
  return entity.parent
    ? `${entity.parent.type}:${entity.parent.id}/${ownKey}`
    : ownKey;
};

export type RefetchCallback = () => void | Promise<void>;

export type ContextualEntityDeeplinkContext = EntityResolutionContext & {
  pathname: string;
  navigate: NavigateFunction;
};

export type ContextualEntityDeeplink = {
  /**
   * Where a mention of this type leads: the callback to run on click, or
   * undefined when the ancestors it needs are still missing after resolution.
   *
   * Called speculatively while the pill renders, with the callback thrown
   * away, so that whatever the click reads is already warm and the click
   * itself stays synchronous. An action body must therefore do nothing but
   * compute that callback and warm its own query.
   */
  action: (
    ref: CopilotResourceRef,
    context: ContextualEntityDeeplinkContext,
  ) => (() => void) | undefined;
};

/**
 * Everything one contextual entity type contributes to the copilot — each
 * type registers exactly one entry in `CONTEXTUAL_ENTITY_DEFINITIONS`.
 */
export type ContextualEntityDefinition = {
  icon: IconDefinition;
  /** Group heading in the @-mention menu. */
  mentionGroupLabel: string;
  /** Singular type label shown on a menu row, e.g. "Flow". */
  typeLabel: string;
  /** Tile color behind `icon` in menu rows. */
  iconColor: NodeIconColor;
  /**
   * The type this one is only addressable within (a node within its
   * flow-version): a mention must carry that parent to be clickable, and the
   * agent is told to reference the two together.
   */
  parentType?: ReferenceContextualEntityType;
  /**
   * Anything about the type the agent cannot infer from the type string.
   * Advertised verbatim, so write it for the agent.
   */
  description?: string;
  /**
   * Completes a ref (incl. parent) from an id via the query client, naming the
   * resource off the record it read to do so. Chain roots (flow, agent) and
   * types not addressable by id alone (node, agent-tool — their parent must
   * come with the mention) omit it, and their pills are named by the mention
   * catalogue or by the label they carried.
   */
  resolve?: ContextualEntityResolver;
  deeplink?: ContextualEntityDeeplink;
};
